<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="7" width-xs="12">
        <vl-title tag-name="h2"> {{ $t('productspecification.detail.title') }} </vl-title>
      </vl-column>
      <vl-column width="5" width-xs="12">
        <vl-action-group mod-align-right mod-space between>
          <template v-if="editStore.canEdit">
            <vl-button
              v-if="items.length === 0"
              icon="plus"
              mod-icon-before
              :mod-disabled="editStore.isCreate(ProductProcessDefinitionEditType.ProductSpecification)"
              @click="editStore.openCreate(ProductProcessDefinitionEditType.ProductSpecification)"
            >
              {{ $t('general.add') }}
            </vl-button>
            <SpecificationActionButton
              v-if="items.length === 0"
              :detail="detail"
              :target="ImportSpecificationType.ProductSpecification"
            />
          </template>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid
      v-if="items && items.length === 1"
      :class="{ edit: editStore.isEdit(ProductProcessDefinitionEditType.ProductSpecification, items[0].id) }"
    >
      <vl-column width="7">
        <ProductSpecificationEdit
          v-if="editStore.isEdit(ProductProcessDefinitionEditType.ProductSpecification, items[0].id)"
          ref="editForm"
          :detail="items[0]"
        />
        <ProductSpecificationRead v-else :detail="items[0]" />
      </vl-column>
      <vl-column width="5">
        <vl-action-group
          v-if="items[0] && !editStore.isEdit(ProductProcessDefinitionEditType.ProductSpecification, items[0].id)"
          mod-align-right
          mod-space
          between
        >
          <vl-button
            v-if="editStore.canEdit"
            icon="pencil"
            mod-icon-before
            mod-secondary
            :mod-disabled="editStore.editing"
            @click="editStore.openEdit(ProductProcessDefinitionEditType.ProductSpecification, items[0].id)"
            >{{ $t('general.edit') }}</vl-button
          >
          <vl-button
            v-if="editStore.canEdit && !hasChildren"
            icon="trash"
            mod-icon-before
            mod-error
            @click="showDeleteModal(items[0].id)"
            >{{ $t('general.delete') }}</vl-button
          >
          <PbsActionButton :label="$t('general.moreActions')" :items="actionButtonItems" mod-large> </PbsActionButton>
        </vl-action-group>
        <vl-action-group v-else mod-align-right mod-space between>
          <vl-button icon="save" x mod-icon-before @click="save">{{ $t('general.save') }}</vl-button>
          <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-accordion-list v-else-if="items && items.length">
      <vl-accordion-list-item v-for="(specification, i) in items" :id="i" :key="i">
        <vl-accordion :id="i.toString()" :title="getLabel(specification)">
          <vl-grid
            :class="{
              edit: editStore.isEdit(ProductProcessDefinitionEditType.ProductSpecification, specification.id)
            }"
          >
            <vl-column width="8">
              <ProductSpecificationEdit
                v-if="editStore.isEdit(ProductProcessDefinitionEditType.ProductSpecification, specification.id)"
                ref="editForm"
                :detail="specification"
              />
              <ProductSpecificationRead v-else :detail="specification" />
            </vl-column>
            <vl-column v-if="editStore.canEdit" width="4">
              <vl-action-group
                v-if="
                  specification &&
                  !editStore.isEdit(ProductProcessDefinitionEditType.ProductSpecification, specification.id)
                "
                mod-align-right
                mod-space
                between
              >
                <vl-button
                  icon="pencil"
                  mod-icon-before
                  mod-secondary
                  :mod-disabled="editStore.editing"
                  @click="editStore.openEdit(ProductProcessDefinitionEditType.ProductSpecification, specification.id)"
                  >{{ $t('general.edit') }}</vl-button
                >
                <vl-button
                  v-if="!hasChildren"
                  icon="trash"
                  mod-icon-before
                  mod-error
                  @click="showDeleteModal(specification.id)"
                  >{{ $t('general.delete') }}</vl-button
                >
              </vl-action-group>
              <vl-action-group v-else mod-align-right mod-space between>
                <vl-button icon="save" x mod-icon-before :mod-loading="reloading" @click="save">{{
                  $t('general.save')
                }}</vl-button>
                <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
                  {{ $t('general.cancel') }}
                </vl-button>
              </vl-action-group>
            </vl-column>
          </vl-grid>
        </vl-accordion>
      </vl-accordion-list-item>
    </vl-accordion-list>
    <vl-grid :class="{ edit: editStore.isCreate(ProductProcessDefinitionEditType.ProductSpecification) }">
      <vl-column>
        <ProductSpecificationCreate v-if="editStore.isCreate(ProductProcessDefinitionEditType.ProductSpecification)" />
      </vl-column>
    </vl-grid>
    <template v-if="items && items.length">
      <vl-grid>
        <vl-column width="12">
          <ProductEntity :specification-id="items[0].id" />
        </vl-column>
      </vl-grid>
      <vl-grid>
        <vl-column width="12">
          <ProductEntityLink :id="items[0].id" />
        </vl-column>
      </vl-grid>
    </template>
    <vl-grid v-else v-vl-spacer:bottom.medium>
      <vl-column>
        <vl-annotation>{{ $t('productspecification.detail.empty') }}</vl-annotation>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import ProductSpecificationCreate from './ProductSpecificationCreate.vue';
import ProductSpecificationRead from './ProductSpecificationRead.vue';
import ProductSpecificationEdit from './ProductSpecificationEdit.vue';
import ProductEntity from '../product-entity/ProductEntity.vue';
import ProductEntityLink from '../product-entity-link/ProductEntityLink.vue';
import SpecificationActionButton from '../specification-action-button/SpecificationActionButton.vue';
import {
  ProductProcessDefinitionDetailDto,
  ProductSpecificationDetailDto,
  ImportSpecificationType
} from '@/api/backoffice/clients';
import { useProductSpecificationStore } from '@/modules/configuration/store/product-specification/product-specification.store';
import { computed, ref, onMounted, Ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  ProductProcessDefinitionEditType,
  useProductProcessDefinitionEditStore
} from '@/modules/configuration/store/product-process-definition/product-process-definition-edit.store';
import { useProductEntityStore } from '@/modules/configuration/store/product-entity/product-entity.store';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useI18n } from 'vue-i18n';
import PbsActionButton, { PbsActionButtonItem } from '@/components/PbsActionButton.vue';
import SpecificationFeatureCatalogForm from '../specification-feature-catalog-form/SpecificiationFeatureCatalogForm.vue';

const store = useProductSpecificationStore();
const editStore = useProductProcessDefinitionEditStore();
const productEntityStore = useProductEntityStore();
const modalStore = useModalStore();

const route = useRoute();
const { t } = useI18n();

const editForm: Ref<typeof ProductSpecificationEdit | (typeof ProductSpecificationEdit)[]> = ref(null);

defineProps<{
  detail: ProductProcessDefinitionDetailDto;
}>();

const loading = computed((): boolean => {
  return store.loading;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const items = computed((): ProductSpecificationDetailDto[] => {
  return store.byDefinition(definitionId.value);
});

const definitionId = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

const actionButtonItems = computed((): PbsActionButtonItem[] => {
  return [
    {
      label: t('productspecification.actions.generateFeatureCatalog.label'),
      click: showGenerateFeatureCatalogModal
    }
  ];
});

const specificationId = computed((): number => {
  return items.value[0]?.id;
});

const hasChildren = computed((): boolean => {
  const productEntities = productEntityStore.bySpecification(specificationId.value);
  return productEntities.length > 0;
});

const showGenerateFeatureCatalogModal = () => {
  const modalContent = {
    title: t('productspecification.actions.generateFeatureCatalog.description'),
    component: SpecificationFeatureCatalogForm,
    props: {
      id: specificationId.value,
      definitionId: definitionId.value
    }
  };

  modalStore.showModal(modalContent);
};

onMounted(async () => {
  await store.getByDefinition(definitionId.value);
});

const save = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const showDeleteModal = (specificationId: number) => {
  modalStore.showDeleteModal({
    title: t('productspecification.detail.delete.title'),
    props: {
      confirm: () => store.remove(definitionId.value, specificationId)
    }
  });
};

const getLabel = (item: ProductSpecificationDetailDto): string => {
  return `${item.label}`;
};
</script>

<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
</style>
